import type { ComponentProps } from 'react';
import React from 'react';
import styled from '@emotion/styled';
import type { DisplayProps, SizeProps } from 'styled-system';
import { display, size } from 'styled-system';

import DefaultProductImage from 'common/components/DefaultProductImage';
import { ResponsiveLazyImageMemo } from 'common/components/LazyImage';
import type { ObjectFitProps } from 'common/theme/system-utilities';
import { objectFit } from 'common/theme/system-utilities';
import type { ImageSizes } from 'common/types/Image';
import { urlToLazyImageProps } from 'common/utils/cdn/image';

export interface ProductImageProps {
  imagePath: string;
  imageAlt: string;
}

const IMG_SIZE_THUMBNAIL = 28;
const IMG_SIZE_S = 56;
const IMG_SIZE_M = 60;
const IMG_SIZE_L = 72;
const IMG_SIZE_XL = 80;

export const IMAGE_SIZES: ImageSizes = {
  thumbnail: IMG_SIZE_THUMBNAIL,
  mobile: IMG_SIZE_S,
  tablet: IMG_SIZE_M,
  desktop: IMG_SIZE_L,
  large_desktop: IMG_SIZE_XL,
};

const UnstyledImage = styled.img<SizeProps & ObjectFitProps & DisplayProps>`
  ${size}
  ${objectFit}
  ${display}
`;

const Image = (props: ComponentProps<typeof UnstyledImage>) => (
  <UnstyledImage
    display="block"
    size={[IMG_SIZE_S, IMG_SIZE_M, IMG_SIZE_L, IMG_SIZE_XL]}
    objectFit="contain"
    {...props}
  />
);

const UnstyledStyledDefaultImage = styled(DefaultProductImage)<SizeProps>`
  ${size}
`;

const StyledDefaultImage = (props: ComponentProps<typeof UnstyledStyledDefaultImage>) => (
  <UnstyledStyledDefaultImage size={[IMG_SIZE_S, IMG_SIZE_M, IMG_SIZE_L, IMG_SIZE_XL]} {...props} />
);

const ProductImage = ({ imageAlt, imagePath }: ProductImageProps) =>
  imagePath ? (
    <ResponsiveLazyImageMemo {...urlToLazyImageProps(imagePath, IMAGE_SIZES)}>
      {({ loaded, image, thumbnail, innerRef }) => (
        <Image
          data-testid="product-card-image"
          ref={innerRef}
          src={loaded ? image : thumbnail}
          alt={imageAlt}
        />
      )}
    </ResponsiveLazyImageMemo>
  ) : (
    <StyledDefaultImage title={imageAlt} />
  );

export default ProductImage;
